"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AddressAutocompleteInput = void 0;
const react_1 = __importStar(require("react"));
const config_1 = require("../config");
const Dropdown_hooks_1 = require("../dropdown/Dropdown.hooks");
const DropdownOption_1 = require("../dropdown/DropdownOption");
const google_maps_api_provider_1 = require("../google-maps-api-provider");
const useClickOutsideHandler_1 = require("../utils/useClickOutsideHandler");
const AddressAutocomplete_hooks_1 = require("./AddressAutocomplete.hooks");
const AddressAutocomplete_styles_1 = require("./AddressAutocomplete.styles");
const AddressAutocomplete_utils_1 = require("./AddressAutocomplete.utils");
const AddressAutocompleteInputInner = (props) => {
    const { isLoaded } = (0, google_maps_api_provider_1.useGoogleApi)();
    const { onAddressChange, restrictToArea, restrictToCountries, restrictToTypes, onPredictionsChanged, ...inputProps } = props;
    const { onBlur, isValid, spacing } = props;
    const { getPlacePredictions, placesService, placePredictions, isPlacePredictionsLoading } = (0, AddressAutocomplete_hooks_1.usePlacesService)({
        restrictedArea: restrictToArea,
        restrictedCountries: restrictToCountries,
        placePredictionsTypes: restrictToTypes,
    });
    const { validateAddress, isValid: isAddressValid } = (0, AddressAutocomplete_hooks_1.useValidateAddress)({
        defaultAddress: props.value,
    });
    const wrapperRef = (0, react_1.useRef)(null);
    const googleAutocompleteRef = (0, react_1.useRef)(null);
    const currentLocation = (0, react_1.useRef)(props.value
        ? {
            name: props.value,
        }
        : null);
    const [isOpen, setIsOpened] = (0, react_1.useState)(false);
    (0, useClickOutsideHandler_1.useClickOutsideHandler)(wrapperRef, isOpen, setIsOpened);
    const callOnChange = (location) => {
        if (googleAutocompleteRef.current) {
            googleAutocompleteRef.current.value = location.name;
        }
        if (onAddressChange) {
            onAddressChange(location);
        }
        setIsOpened(false);
        googleAutocompleteRef.current?.blur();
    };
    const setAddress = (description, placeId) => {
        placesService?.getDetails({
            placeId,
            fields: ["formatted_address", "geometry", "address_components"],
        }, (placeDetails, status) => {
            if (!placeDetails || status !== "OK") {
                return;
            }
            currentLocation.current = (0, AddressAutocomplete_utils_1.parsePlaceResultResponse)(description, placeDetails);
            callOnChange(currentLocation.current);
        });
    };
    const placePredictionsWithinRadius = (0, react_1.useMemo)(() => placePredictions.filter((prediction) => (0, AddressAutocomplete_utils_1.isPlaceWithinRadius)(prediction, restrictToArea)), [placePredictions]);
    const { focused, setFocused, handleArrowNavigation } = (0, Dropdown_hooks_1.useDropdownArrowKeysNavigation)(false, true, placePredictionsWithinRadius.map((place) => ({
        label: place.description,
        value: place.place_id,
    })), (option) => {
        setAddress(option.label, option.value.toString());
    }, setIsOpened);
    const onInputChange = (inputValue) => {
        setFocused(undefined);
        setIsOpened(!!googleAutocompleteRef?.current?.value);
        getPlacePredictions(inputValue);
        if (onAddressChange) {
            onAddressChange({ name: inputValue, countryIsoCode: "", position: undefined });
        }
    };
    const onFocus = (event) => {
        if (!googleAutocompleteRef.current) {
            throw Error("Ref is not set for google autocomplete input.");
        }
        googleAutocompleteRef.current.setAttribute("autocomplete", `autocomplete-${Math.random()}`);
        if (placePredictionsWithinRadius.length) {
            setIsOpened(true);
        }
        if (inputProps.onFocus) {
            inputProps.onFocus(event);
        }
    };
    const handleBlur = async (event) => {
        const hasFocusedOut = wrapperRef.current && !wrapperRef.current.matches(":focus-within");
        validateAddress(currentLocation?.current?.name || "", { area: restrictToArea, countries: restrictToCountries });
        if (hasFocusedOut) {
            setIsOpened(false);
            if (onBlur) {
                onBlur(event);
            }
        }
    };
    (0, react_1.useEffect)(() => {
        // If the input has an exact match from results, we enforce it
        // This accounts for manually entered/modified addresses (copy/paste, delete/undos, etc)
        const inputValue = googleAutocompleteRef.current?.value;
        const resultMatch = placePredictionsWithinRadius.find((prediction) => prediction.description === inputValue);
        if (resultMatch) {
            setAddress(resultMatch.description, resultMatch.place_id);
        }
        if (onPredictionsChanged) {
            onPredictionsChanged(placePredictions);
        }
    }, [placePredictions]);
    const isOpenAndHasResults = isOpen && !isPlacePredictionsLoading && placePredictionsWithinRadius.length > 0;
    function isInputValid() {
        if (isOpen) {
            return true;
        }
        // If the validity is explicitly set, use that
        if (isValid !== undefined && isValid !== null) {
            return isValid;
        }
        if (googleAutocompleteRef.current?.value) {
            return isAddressValid;
        }
        return true;
    }
    if (!isLoaded) {
        return null;
    }
    return (react_1.default.createElement(AddressAutocomplete_styles_1.DropdownWrapper, { ref: wrapperRef, onKeyDown: handleArrowNavigation, spacing: spacing },
        react_1.default.createElement(AddressAutocomplete_styles_1.StyledInput, { ...inputProps, isAutocompleteDisabled: true, inputRef: googleAutocompleteRef, onChange: onInputChange, onFocus: onFocus, onBlur: handleBlur, isOpen: isOpenAndHasResults, isPlacePredictionsLoading: isPlacePredictionsLoading, isValid: isInputValid(), ariaInvalid: !isInputValid() }),
        isOpenAndHasResults && (react_1.default.createElement(AddressAutocomplete_styles_1.DropdownBody, { isValid: isInputValid(), tabIndex: isOpen ? -1 : undefined },
            placePredictionsWithinRadius.map(({ description, place_id }, index) => (react_1.default.createElement(DropdownOption_1.DropdownOption, { "data-cy": "addressOption", key: place_id, content: { label: description, value: place_id }, isFocused: focused === index, onSelect: () => {
                    setAddress(description, place_id);
                    setFocused(index);
                } }))),
            react_1.default.createElement(AddressAutocomplete_styles_1.PoweredByGoogle, null,
                react_1.default.createElement("img", { src: `${config_1.BASE_IMAGE_URL}/site/powered_by_google_on_white_hdpi.png`, alt: "Powered by Google", width: 144, height: 18 }))))));
};
const AddressAutocompleteInput = (props) => {
    const { isLoaded } = (0, google_maps_api_provider_1.useGoogleApi)();
    /**
     * The AddressAutocompleteInputInner component uses the Google Maps api through `usePlacesService`.
     * The `usePlacesService` hook will try to load the Places library as soon as it enters the component tree.
     * This can cause a race condition where the Places library is not loaded yet when the `usePlacesService` hook is called,
     * thereby causing an error when `usePlacesService` tries to new up a `google.maps.places.AutocompleteService` instance.
     *
     * Note: this happens when the `AddressAutocompleteInput` is rendered on the first pass when rendering the page,
     * not when it is rendered on a subsequent pass (e.g. when the user clicks on a button to show the input).
     * */
    if (!isLoaded) {
        // We return a skeleton instead of `null` to avoid the UI jumping around
        return (react_1.default.createElement(AddressAutocomplete_styles_1.DropdownWrapper, { spacing: props.spacing },
            react_1.default.createElement(AddressAutocomplete_styles_1.StyledInput, { isOpen: false })));
    }
    return react_1.default.createElement(AddressAutocompleteInputInner, { ...props });
};
exports.AddressAutocompleteInput = AddressAutocompleteInput;
//# sourceMappingURL=AddressAutocomplete.js.map